import { CaretDown } from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import {
  Button as AriaButton,
  ComboBox as AriaComboBox,
  type ComboBoxProps as AriaComboBoxProps,
  Group,
  Input,
  ListBox,
} from "react-aria-components";
import { cx } from "@/lib/cx";
import {
  type BaseFieldProps,
  controlHeight,
  FieldDescription,
  FieldErrorText,
  FieldLabel,
  fieldRoot,
  inputBase,
} from "./field";
import { listPad } from "./list-styles";
import { Popover } from "./Popover";
import { type Option, OptionItem } from "./Select";

export interface ComboBoxProps
  extends Omit<AriaComboBoxProps<Option>, "children" | "className" | "items" | "selectedKey" | "onSelectionChange">,
    BaseFieldProps {
  items: Option[];
  selectedKey?: string | null;
  onSelectionChange?: (key: string | null) => void;
  placeholder?: string;
  className?: string;
  /** Extra classes for the text input (e.g. a larger font on touch screens). */
  inputClassName?: string;
}

/** Type-to-filter picker. Filtering is built in (contains, locale aware); pass `items` only. */
export function ComboBox({
  label,
  labelHidden,
  description,
  error,
  items,
  selectedKey,
  onSelectionChange,
  placeholder,
  className,
  inputClassName,
  ...props
}: ComboBoxProps) {
  return (
    <AriaComboBox
      className={cx(fieldRoot, className)}
      defaultItems={items}
      selectedKey={selectedKey}
      onSelectionChange={(k) => onSelectionChange?.(k == null ? null : String(k))}
      isInvalid={error ? true : props.isInvalid}
      menuTrigger="focus"
      {...props}
    >
      <FieldLabel hidden={labelHidden} required={props.isRequired}>
        {label}
      </FieldLabel>
      <Group className="relative flex items-center">
        <Input placeholder={placeholder} className={cx(inputBase, controlHeight, "pr-9", inputClassName)} />
        <AriaButton className="absolute right-0.5 inline-flex size-(--control-h-sm) items-center justify-center rounded-control text-fg-muted data-hovered:bg-hover data-pressed:bg-active">
          <CaretDown size={12} aria-hidden="true" />
        </AriaButton>
      </Group>
      {description ? <FieldDescription>{description}</FieldDescription> : null}
      <FieldErrorText>{error}</FieldErrorText>
      <Popover className="min-w-(--trigger-width) max-h-72">
        <ListBox
          items={items}
          className={listPad}
          renderEmptyState={() => <div className="px-2 py-2 text-sm text-fg-muted">{m.combo_no_results()}</div>}
        >
          {(o) => <OptionItem option={o} />}
        </ListBox>
      </Popover>
    </AriaComboBox>
  );
}
